import { useEffect, useCallback, useRef, useState, lazy, Suspense } from "react";
import { ToastContainer } from "./components/Toast";
import { ScriptViewDialog } from "./components/editor/ScriptViewDialog";
import { SearchModal } from "./components/editor/SearchModal";
import { MobileBlocker } from "./components/MobileBlocker";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { WelcomeScreen } from "./components/welcome";
import { OnboardingGate } from "./components/onboarding";
import { SharePage } from "./pages/SharePage";
import { useUIStore } from "./stores/ui-store";
import { useProjectStore } from "./stores/project-store";
import { useAuthStore } from "./stores/auth-store";
import { useRouter } from "./hooks/use-router";
import { useKieAIPoller } from "./hooks/useKieAIPoller";
import { useGpuJobPoller } from "./hooks/useGpuJobPoller";
import { checkForRecovery } from "./services/auto-save";
import { SOCIAL_MEDIA_PRESETS, type SocialMediaCategory } from "@openreel/core";
import { ToolcraftText as Text } from "@openreel/ui";
import { MOTION_DESIGN_ENABLED } from "./components/WorkspaceModeTabs";

const EditorInterface = lazy(() =>
  import("./components/editor/EditorInterface").then((m) => ({
    default: m.EditorInterface,
  }))
);
const MotionCreatorApp = lazy(() =>
  import("./motion/MotionCreatorApp").then((module) => ({
    default: module.MotionCreatorApp,
  }))
);

const LoadingSpinner: React.FC<{ message: string }> = ({ message }) => (
  <div className="h-screen w-screen bg-background flex flex-col items-center justify-center">
    <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
    <Text type="supporting" color="secondary" className="text-sm text-text-secondary">{message}</Text>
  </div>
);

/**
 * The project the editor last had open on this device.
 *
 * A reload starts the store empty, so nothing in memory can say what the user
 * was in. The auto-saves outlive the reload but only record which projects
 * exist, and the newest of them is a guess once a second project has been
 * touched. This is the missing half: the id, written whenever a project is
 * open, read once on the way back in.
 */
const LAST_PROJECT_KEY = "flshy.lastProject";

function readLastProjectId(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT_KEY);
  } catch {
    // Storage blocked: resume falls back to the newest auto-save.
    return null;
  }
}

/**
 * Puts the user back where they were, or leaves the store untouched when there
 * is genuinely nothing to put back.
 *
 * Module scope on purpose: the resume must survive a StrictMode remount as one
 * attempt, so App holds the promise rather than the work.
 */
async function resumeLastProject(): Promise<void> {
  try {
    // Newest first, and scoped to the signed-in account by the manager.
    const saves = await checkForRecovery();
    const lastProjectId = readLastProjectId();
    const save =
      saves.find((s) => s.projectId === lastProjectId) ?? saves[0] ?? null;
    if (!save) return;
    await useProjectStore.getState().recoverFromAutoSave(save.id);
  } catch (error) {
    // A resume that cannot complete is the old behaviour, not a new failure:
    // say so and let the caller fall through to the home screen rather than
    // holding the user on a spinner forever.
    console.error("[App] Could not resume the last project:", error);
  }
}

const PRESET_DIMENSIONS: Record<string, SocialMediaCategory> = {
  "1080x1920": "tiktok",
  "1920x1080": "youtube-video",
  "1080x1080": "instagram-post",
  "720x1280": "instagram-stories",
  "1280x720": "youtube-video",
};

function App() {
  const { activeModal, closeModal, skipWelcomeScreen } = useUIStore();
  const { openModal: openSearchModal } = useUIStore();
  const createNewProject = useProjectStore((state) => state.createNewProject);
  const isSignedInAndOnboarded = useAuthStore(
    (state) => Boolean(state.user) && Boolean(state.onboarding?.completed),
  );

  const { route, params, navigate, parsedDimensions, fps } = useRouter();
  const hasHandledInitialRoute = useRef(false);
  const isMotionHost =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("motion.");
  // Motion Design is parked for v2 (see MOTION_DESIGN_ENABLED). With the flag
  // off the surface is unreachable, so the motion host and the /motion route
  // both fall through to the normal editor rather than mounting MotionCreatorApp.
  const isMotionSurface =
    MOTION_DESIGN_ENABLED && (isMotionHost || route === "motion");

  useKieAIPoller();
  useGpuJobPoller();

  useEffect(() => {
    if (hasHandledInitialRoute.current) return;

    if (isMotionSurface) {
      hasHandledInitialRoute.current = true;
    } else if (route === "new") {
      hasHandledInitialRoute.current = true;

      let projectName = "New Project";
      let width = 1920;
      let height = 1080;
      let frameRate = fps;

      if (params.preset) {
        const presetKey = params.preset as SocialMediaCategory;
        const preset = SOCIAL_MEDIA_PRESETS[presetKey];
        if (preset) {
          width = preset.width;
          height = preset.height;
          frameRate = preset.frameRate || fps;
          projectName = `New ${presetKey.charAt(0).toUpperCase() + presetKey.slice(1).replace(/-/g, " ")} Project`;
        }
      } else if (parsedDimensions) {
        width = parsedDimensions.width;
        height = parsedDimensions.height;

        const dimensionKey = `${width}x${height}`;
        const matchingPreset = PRESET_DIMENSIONS[dimensionKey];
        if (matchingPreset) {
          const preset = SOCIAL_MEDIA_PRESETS[matchingPreset];
          frameRate = preset.frameRate || fps;
        }

        const aspectRatio = width / height;
        if (aspectRatio < 1) {
          projectName = "New Vertical Video";
        } else if (aspectRatio > 1) {
          projectName = "New Horizontal Video";
        } else {
          projectName = "New Square Video";
        }
      }

      createNewProject(projectName, { width, height, frameRate });
      navigate("editor");
    } else if (route === "editor" && skipWelcomeScreen) {
      hasHandledInitialRoute.current = true;
    } else if (["welcome", "templates", "recent"].includes(route)) {
      hasHandledInitialRoute.current = true;
    }
  }, [
    route,
    isMotionSurface,
    params,
    parsedDimensions,
    fps,
    createNewProject,
    navigate,
    skipWelcomeScreen,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Escape is a shortcut out of the dashboard, not a way into the app.
      // This listener sits above the onboarding gate, so it used to fire on
      // the sign-in and OTP screens too: one press to dismiss a password
      // manager rewrote the hash to #/editor, and because nothing ever writes
      // the hash back, sign-in then completed straight into an empty editor.
      if (e.key === "Escape" && route !== "editor" && isSignedInAndOnboarded) {
        navigate("editor");
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        openSearchModal("search");
      }
    },
    [route, navigate, openSearchModal, isSignedInAndOnboarded],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Remember what is open, for the resume below. Written on every change of
  // the open project rather than on unload: a reload, a crash and a closed tab
  // all skip whatever we would have hung off unload.
  useEffect(
    () =>
      useProjectStore.subscribe(
        (state) => (state.hasOpenProject ? state.project.id : null),
        (projectId) => {
          if (!projectId) return;
          try {
            localStorage.setItem(LAST_PROJECT_KEY, projectId);
          } catch {
            // Storage blocked: resume falls back to the newest auto-save.
          }
        },
        { fireImmediately: true },
      ),
    [],
  );

  // Reloading the editor lands back on #/editor with an empty store, so the
  // guard below saw hasOpenProject false and sent the user home with their
  // work apparently gone. The auto-saves survive the reload, so read them
  // before concluding there is nothing here. Only on a fresh mount into the
  // editor: every in-app route into it opens a project itself.
  const [resumeState, setResumeState] = useState<"pending" | "settled">(() =>
    route === "editor" && !useProjectStore.getState().hasOpenProject
      ? "pending"
      : "settled",
  );
  const resumeAttempt = useRef<Promise<void> | null>(null);
  useEffect(() => {
    if (resumeState !== "pending") return;
    // Auto-saves are scoped to the account, so none are listable until the
    // session is back. The redirect below waits on the same condition, which
    // is why waiting here cannot strand anyone in the editor.
    if (!isSignedInAndOnboarded) return;
    // Held across a StrictMode remount so the same save is not recovered
    // twice; a settle after unmount is a no-op.
    resumeAttempt.current ??= resumeLastProject();
    void resumeAttempt.current.then(() => setResumeState("settled"));
  }, [resumeState, isSignedInAndOnboarded]);

  // The hash is sticky: everything writes #/editor and nothing writes it back,
  // so a sign-out and sign-in, a reload, or the old Escape slip all re-enter
  // the editor. The store knows the difference: an auto-created project that
  // nobody opened leaves hasOpenProject false, and that is the case where the
  // dashboard is what was wanted. A real deep link opens a project first, so
  // this does not fire on one.
  const routedHome = useRef(false);
  useEffect(() => {
    if (routedHome.current || !isSignedInAndOnboarded) return;
    if (route !== "editor") {
      routedHome.current = true;
      return;
    }
    // hasOpenProject is read synchronously and the auto-save read is not, so
    // without this the redirect wins the race and bounces the user home while
    // their project is still on its way back.
    if (resumeState === "pending") return;
    if (!useProjectStore.getState().hasOpenProject) {
      routedHome.current = true;
      navigate("welcome");
    }
  }, [isSignedInAndOnboarded, route, navigate, resumeState]);

  const showWelcome =
    ["welcome", "templates", "recent"].includes(route) && !skipWelcomeScreen;
  const initialTab =
    route === "templates"
      ? "templates"
      : route === "recent"
        ? "recent"
        : undefined;
  const isSharePage = route === "share" && params.shareId;

  return (
    <div className="h-screen w-screen bg-background text-text-primary overflow-hidden">
      <MobileBlocker />
      {isMotionSurface ? (
        <Suspense fallback={<LoadingSpinner message="Loading Motion Creator..." />}>
          <MotionCreatorApp />
        </Suspense>
      ) : isSharePage ? (
        // Share links stay public: a viewer should never be asked to sign in.
        <SharePage shareId={params.shareId!} />
      ) : (
        <OnboardingGate>
          {showWelcome ? (
            <WelcomeScreen initialTab={initialTab} />
          ) : resumeState === "pending" ? (
            // The same message the editor's own fallback uses. Nothing has
            // been read yet, so promising a restore here would be a promise we
            // cannot keep on a session that has nothing to restore.
            <LoadingSpinner message="Loading editor..." />
          ) : (
            <Suspense fallback={<LoadingSpinner message="Loading editor..." />}>
              <EditorInterface />
            </Suspense>
          )}
          {/* Still no auto-popping recovery dialog: it interrupted every load.
              Reloading straight back into the editor now resumes the last
              project silently instead (see resumeLastProject), and the
              autosaves stay reachable from the home's Recent Projects, which
              reads the same data. */}
        </OnboardingGate>
      )}
      <ToastContainer />
      <InstallPrompt />
      <ScriptViewDialog
        isOpen={activeModal === "scriptView"}
        onClose={closeModal}
      />
      <SearchModal isOpen={activeModal === "search"} onClose={closeModal} />
    </div>
  );
}

export default App;
